// CreateCajaRecord.tsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { X, EyeSlash, Eye, Calculator, InfoCircle, JournalText, ExclamationTriangle, CheckCircle, Play, ArrowRight } from 'react-bootstrap-icons';
import './CreateCajaRecord.scss';
import { getCajaMovementSessionSummaryByLocalId } from '../../../../../../../../crud/payments_api/caja_movements';
import { getSessionPaymentTransactionSummaryByLocalId } from '../../../../../../../../crud/payments_api/payment_transactions';
import { getRealTimeSalesStatisticsByLocalId } from '../../../../../../../../crud/management_api/sales_statistics';
import { createCajaSessionRecord } from '../../../../../../../../crud/management_api/caja_session_records';
import { getLocalCajaManagementByLocalId } from '../../../../../../../../crud/management_api/locales_caja_management';
import { endLocalSession, getActiveLocalSessionByLocalId } from '../../../../../../../../crud/management_api/local_sessions';
import { flushLastSessionByLocalId } from '../../../../../../../../crud/payments_api/cache';
import { PaymentTransactionSummary, CajaMovementSessionSummary, CajaRecordType } from '../../types';
import { toast } from 'react-toastify';
import MiniLoadingSpinner from '../../../../../../../../components/Spinners/MiniLoadingSpinner';
import TransactionSummary from './components/TransactionSummary/TransactionSummary';
import CajaSummary from './components/CajaSummary/CajaSummary';
import TipSummary from './components/TipSummary/TipSummary';
import InfoSection from './components/InfoSection/InfoSection';
import { formatCurrency } from '../../../../../../../../utils/functions';
import Spinner from 'react-bootstrap/Spinner';
import { getPermissionState } from '../../../../../../../../utils/functions';

interface RenderModalProps {
  show: boolean;
  setShow: (show: boolean) => void;
  onRecordCreated?: () => void;
  hideCloseSession?: boolean; // When true, hides the "Crear y cerrar sesión" button (used in shift context)
}

// Workflow steps for bundled create+close
type ProcessStep = 'setup' | 'creating-record' | 'ending-session' | 'completed';

const CreateCajaRecord: React.FC<RenderModalProps> = ({ show, setShow, onRecordCreated, hideCloseSession = false }) => {
  const [transactionSummary, setTransactionSummary] = useState<PaymentTransactionSummary | null>(null);
  const [cajaSummary, setCajaSummary] = useState<CajaMovementSessionSummary | null>(null);
  const [cajasStatus, setCajasStatus] = useState<any[]>([]);
  const [tipSummary, setTipSummary] = useState<any>(null);
  const [userReportedData, setUserReportedData] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [transactionDifference, setTransactionDifference] = useState(0);
  const [cajaDifference, setCajaDifference] = useState(0);
  const [tipDifference, setTipDifference] = useState(0);
  const [showBalances, setShowBalances] = useState(false); // Controls balance visibility
  const [hasViewPermission, setHasViewPermission] = useState(false);
  const [showInfo, setShowInfo] = useState(false); // Controls info modal visibility
  const [notes, setNotes] = useState(''); // Notes for the arqueo
  const [showNotesInput, setShowNotesInput] = useState(false); // Controls notes input visibility

  // Bundled workflow state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentStep, setCurrentStep] = useState<ProcessStep>('setup');
  const [recordCreated, setRecordCreated] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const isProcessingWorkflow = currentStep !== 'setup' && currentStep !== 'completed';

  // Handle modal close with cleanup
  const handleCloseModal = () => {
    setShow(false);
    setNotes('');
    setShowNotesInput(false);
    // reset workflow
    setShowConfirmModal(false);
    setCurrentStep('setup');
    setRecordCreated(false);
    setSessionEnded(false);
  };

  // Build caja record object (shared between single approve and bundled workflow)
  const buildCajaRecord = (): CajaRecordType | null => {
    if (!transactionSummary || !cajaSummary) return null;

    const details = {
      types: finalTransactionSummary,
      caja_movements: finalCajaMovements,
      tips: finalTipSummary,
    };

    const movementsDifference = transactionDifference + cajaDifference;
    const overallDifference = movementsDifference + tipDifference;

    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const userId = user ? Number(user.user_id) : 0;
    const localId = user ? Number(user.local_id) : 0;
    const username = user ? user.user_name : '';

    const cajaRecord: CajaRecordType = {
      caja_session_record_id: Date.now(),
      local_id: localId,
      session_id: 0,
      user_id: userId,
      user_name: username,
      session_start: '2024-11-15T08:00:00Z',
      session_end: '2024-11-15T18:00:00Z',
      initial_amount: cajaSummary.total_opening_balance,
      final_amount: cajaSummary.total_final_amount,
      details,
      difference: overallDifference,
      movements_matched: Math.abs(movementsDifference) < 0.01,
      movements_difference: movementsDifference,
      tips_matched: Math.abs(tipDifference) < 0.01,
      tip_difference: tipDifference,
      notes: notes,
      local_timestamp: new Date().toISOString(),
      timestamp: new Date().toISOString(),
    };
    return cajaRecord;
  };

  const handleCreateAndClose = () => {
    // Show confirmation modal first
    if (!buildCajaRecord()) {
      toast.error('Faltan datos para crear el arqueo');
      return;
    }
    setShowConfirmModal(true);
  };

  const executeBundledWorkflow = async () => {
    if (isProcessingWorkflow) return;
    const cajaRecord = buildCajaRecord();
    if (!cajaRecord) {
      toast.error('Faltan datos');
      return;
    }
    try {
      setCurrentStep('creating-record');
      await createCajaSessionRecord(cajaRecord);
      setRecordCreated(true);
      setCurrentStep('ending-session');

      // Fetch active session and end it
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const localId = user ? Number(user.local_id) : 0;
      try {
        const activeSessionRes = await getActiveLocalSessionByLocalId(localId);
        const activeSession = activeSessionRes?.data;
        if (!activeSession || !activeSession.local_session_id) {
          throw new Error('No hay sesión activa para cerrar');
        }
        await endLocalSession(activeSession.local_session_id);
        
        // Flush cache immediately after successful session end
        try {
          if (localId) {
            await flushLastSessionByLocalId(localId);
          }
        } catch (cacheError) {
          console.error('Error flushing cache (non-critical):', cacheError);
          // Don't fail the whole operation if cache flush fails
        }
        
        setSessionEnded(true);
        setCurrentStep('completed');
        toast.success('Arqueo creado y sesión cerrada');
        if (onRecordCreated) onRecordCreated();
        // Redirect after short delay
        setTimeout(() => {
          window.location.assign('/App');
        }, 1300);
      } catch (e: any) {
        console.error('Error cerrando sesión:', e);
        toast.error(e?.response?.data?.message || e?.message || 'Error cerrando la sesión');
        setCurrentStep('completed'); // Mark as done (partial) to unblock UI
      }
    } catch (error: any) {
      console.error('Error en workflow:', error);
      toast.error(error?.response?.data?.message || error?.message || 'Error creando arqueo');
      setCurrentStep('setup');
      setRecordCreated(false);
      setSessionEnded(false);
    }
  };

  // Calculate epsilon for floating point comparisons


  // New states to hold the final merged/tagged info from the children
  const [finalTransactionSummary, setFinalTransactionSummary] = useState<any>({});
  const [finalCajaMovements, setFinalCajaMovements] = useState<any>({});
  const [finalTipSummary, setFinalTipSummary] = useState<any>({});

  // Validation: compute which inputs are required and which are missing
  const getRequiredInputKeys = (): string[] => {
    const keys: string[] = [];
    
    // Transaction types - use finalTransactionSummary (which reflects combined types) instead of original transactionSummary
    if (Object.keys(finalTransactionSummary).length > 0) {
      Object.keys(finalTransactionSummary)
        .filter(type => type !== 'Efectivo')
        .forEach(type => keys.push(type));
    } else if (transactionSummary?.type_summary) {
      // Fallback to original if finalTransactionSummary not yet populated
      Object.keys(transactionSummary.type_summary)
        .filter(type => type !== 'Efectivo')
        .forEach(type => keys.push(type));
    }
    
    // Caja workstations
    if (cajaSummary?.cajas) {
      Object.keys(cajaSummary.cajas).forEach(workstationId => {
        keys.push(`caja_${workstationId}`);
      });
    }
    
    // Tips (only if showBalances is true, since TipSummary is hidden otherwise)
    if (showBalances && tipSummary?.tips) {
      Object.keys(tipSummary.tips).forEach(item => {
        keys.push(`item_${item}`);
      });
    }
    
    return keys;
  };

  const getMissingInputs = (): string[] => {
    const required = getRequiredInputKeys();
    return required.filter(key => {
      const value = userReportedData[key];
      // Consider empty if undefined, null, or empty string
      return value === undefined || value === null || value === '';
    });
  };

  const missingInputs = getMissingInputs();
  const hasAllInputsFilled = missingInputs.length === 0;

  useEffect(() => {
    const hasPermission = getPermissionState(132);
    setHasViewPermission(hasPermission);
    setShowBalances(hasPermission); // Only show balances if user has permission
  }, [show]);

  useEffect(() => {
    if (show) {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user') ?? '{}');
      const localId = user ? Number(user.local_id) : 0;
  
      Promise.all([
        getCajaMovementSessionSummaryByLocalId(localId),
        getSessionPaymentTransactionSummaryByLocalId(localId),
        getRealTimeSalesStatisticsByLocalId(localId),
        getLocalCajaManagementByLocalId(localId)
      ])
        .then(([cajaRes, transactionRes, statisticRes, cajasStatusRes]) => {
          setCajaSummary(cajaRes.data);
          setTransactionSummary(transactionRes.data);
          setCajasStatus(cajasStatusRes.data);
          
          // Get tip amount from active shift if exists, otherwise use total tip
          let totalTipAmount = statisticRes.data.total_tip;
          let activeShift = null;
          let activeShiftId = null;
          
          // Check if there's an active shift and use its tip amount instead
          if (statisticRes.data.shifts) {
            activeShift = Object.values(statisticRes.data.shifts).find((shift: any) => shift.active === true);
            if (activeShift && (activeShift as any).total_tip !== undefined) {
              totalTipAmount = (activeShift as any).total_tip;
              activeShiftId = (activeShift as any).shift_id;
            }
          }
          
          // Calculate cash vs card proportion and tips
          let cashAmount = 0;
          let cardAmount = 0;
          let cashTipFromTransactions = 0;
          let cardTipFromTransactions = 0;
          
          const transactionSummaryData = transactionRes.data;
          
          // Prefer API tip breakdown when present. If both sources provide shift ids,
          // only trust it when they match; if one side is missing, still use breakdown.
          const transactionShiftId = transactionSummaryData?.active_shift_id;
          const hasTypeSummary = Boolean(transactionSummaryData?.type_summary);
          const hasTipBreakdown = hasTypeSummary && Object.values(transactionSummaryData.type_summary).some((entry: any) => {
            const tip = entry?.tip_amount;
            return typeof tip === 'number' && !Number.isNaN(tip);
          });
          const hasBothShiftIds = activeShiftId !== null && activeShiftId !== undefined && transactionShiftId !== null && transactionShiftId !== undefined;
          const shiftsMatch = Number(activeShiftId) === Number(transactionShiftId);
          const useTransactionTipBreakdown = hasTipBreakdown && (!hasBothShiftIds || shiftsMatch);
          
          if (useTransactionTipBreakdown) {
            // Use tip amounts directly from transaction summary (already filtered by active shift)
            Object.entries(transactionSummaryData.type_summary).forEach(([paymentType, data]: [string, any]) => {
              const amount = data.amount || 0;
              const tipAmount = data.tip_amount || 0;
              
              if (paymentType.toLowerCase().includes('efectivo') || paymentType.toLowerCase().includes('cash')) {
                cashAmount += amount;
                cashTipFromTransactions += tipAmount;
              } else {
                cardAmount += amount;
                cardTipFromTransactions += tipAmount;
              }
            });
          } else if (activeShift && (activeShift as any).type_stats) {
            // Fallback: use active shift's type_stats for proportions
            Object.entries((activeShift as any).type_stats).forEach(([paymentType, data]: [string, any]) => {
              const amount = data.total || 0;
              
              if (paymentType.toLowerCase().includes('efectivo') || paymentType.toLowerCase().includes('cash')) {
                cashAmount += amount;
              } else {
                cardAmount += amount;
              }
            });
          } else if (transactionSummaryData && transactionSummaryData.type_summary) {
            // Last fallback: use full session transaction summary
            Object.entries(transactionSummaryData.type_summary).forEach(([paymentType, data]: [string, any]) => {
              const amount = data.amount || 0;
              
              if (paymentType.toLowerCase().includes('efectivo') || paymentType.toLowerCase().includes('cash')) {
                cashAmount += amount;
              } else {
                cardAmount += amount;
              }
            });
          }
          
          // Calculate tip distribution
          const totalTransactionAmount = cashAmount + cardAmount;
          let cashTipAmount = 0;
          let cardTipAmount = totalTipAmount;
          
          if (useTransactionTipBreakdown) {
            // Use exact tip amounts from transaction summary (already filtered by active shift)
            cashTipAmount = cashTipFromTransactions;
            cardTipAmount = cardTipFromTransactions;
          } else if (totalTransactionAmount > 0) {
            // Calculate proportionally based on payment amounts
            const cashProportion = cashAmount / totalTransactionAmount;
            cashTipAmount = Number((totalTipAmount * cashProportion).toFixed(2));
            cardTipAmount = Number((totalTipAmount - cashTipAmount).toFixed(2));
          }
          
          // Create structured tip summary with automatic separation
          setTipSummary({ 
            "tips": { 
              "Propina_Efectivo": { 
                "amount": cashTipAmount,
                "description": "Propinas en efectivo"
              },
              "Propina_Tarjeta": { 
                "amount": cardTipAmount,
                "description": "Propinas con tarjeta y otros métodos"
              }
            } 
          });
        })
        .catch((error) => {
          console.error('Error fetching summaries: ', error);
          toast.error('Error obteniendo datos');
        })
        .finally(() => setLoading(false));
    }
  }, [show]);
  
  
  const handleReportedAmountChange = (key: string, value: number) => {
    setUserReportedData((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };
  
  const handleApprove = () => {
    if (approving) return;
    setApproving(true);
    const cajaRecord = buildCajaRecord();
    if (!cajaRecord) {
      toast.error('Faltan datos para aprobar el cierre de caja');
      setApproving(false);
      return;
    }
    createCajaSessionRecord(cajaRecord)
      .then(() => {
        toast.success('Cierre de caja aprobado');
        handleCloseModal();
        if (onRecordCreated) onRecordCreated();
      })
      .catch((error) => {
        console.error('Error creating caja record: ', error);
        if (error?.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error?.message) {
          toast.error(error.message);
        } else {
          toast.error('Error aprobando cierre de caja');
        }
      })
      .finally(() => setApproving(false));
  };
  
  const renderForm = () => (
    <Form>
      <TransactionSummary
        transactionSummary={transactionSummary}
        userReportedData={userReportedData}
        handleReportedAmountChange={handleReportedAmountChange}
        setTransactionDifference={setTransactionDifference}
        onFinalTransactionSummaryChange={setFinalTransactionSummary}
        showBalances={showBalances}
        missingInputs={missingInputs}
      />
      <CajaSummary
        cajaSummary={cajaSummary}
        cajasStatus={cajasStatus}
        handleReportedAmountChange={handleReportedAmountChange}
        userReportedData={userReportedData}
        setCajaDifference={setCajaDifference}
        onFinalCajaMovementsChange={setFinalCajaMovements}
        showBalances={showBalances}
        missingInputs={missingInputs}
        tipSummary={tipSummary}
      />
      <TipSummary
        tipSummary={tipSummary}
        handleReportedAmountChange={handleReportedAmountChange}
        userReportedData={userReportedData}
        setTipDifference={setTipDifference}
        onFinalTipSummaryChange={setFinalTipSummary}
        showBalances={showBalances}
        missingInputs={missingInputs}
      />
      {showBalances && (
        <div
          className={`total-difference-row highlighted-section ${
            Math.abs(transactionDifference + cajaDifference + tipDifference) < 0.0001 * (Math.abs(transactionDifference + cajaDifference + tipDifference) || 1)
              ? 'difference-green'
              : ((transactionDifference + cajaDifference + tipDifference) > 0 ? 'difference-green' : 'difference-red')
          }`}
        >
          <div className="total-difference-content">
            <div className="difference-icon">
              <Calculator />
            </div>
            <div className="total-difference-label">Diferencia Total</div>
            <div
              className={`total-difference-value ${
                Math.abs(transactionDifference + cajaDifference + tipDifference) < 0.0001 * (Math.abs(transactionDifference + cajaDifference + tipDifference) || 1)
                  ? 'difference-green'
                  : ((transactionDifference + cajaDifference + tipDifference) > 0 ? 'difference-green' : 'difference-red')
              }`}
            >
              {formatCurrency(
                Math.abs(transactionDifference + cajaDifference + tipDifference) < 0.0001 * (Math.abs(transactionDifference + cajaDifference + tipDifference) || 1)
                  ? 0
                  : transactionDifference + cajaDifference + tipDifference
              )}
            </div>
          </div>
        </div>
      )}
    </Form>
  );
  
  return (
    <>
      <Modal 
        show={show} 
        onHide={handleCloseModal} 
        centered 
        className="modal-arqueo-caja-container"
      >
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">Arqueo de Caja</Modal.Title>
          </div>
          <div className="header-buttons">
            <Button 
              variant="none" 
              className="balance-toggle-button"
              onClick={() => setShowInfo(!showInfo)}
              title={showInfo ? "Ocultar información" : "Mostrar información"}
            >
              <InfoCircle size={20} />
            </Button>
            <Button 
              variant="none" 
              className="balance-toggle-button"
              onClick={() => setShowBalances(!showBalances)}
              title={showBalances ? "Ocultar balances" : "Mostrar balances"}
            >
              { hasViewPermission && <> {showBalances  ? <EyeSlash size={20} /> : <Eye size={20} />}</>}
            </Button>
            <Button 
              variant="none" 
              className={`balance-toggle-button ${showNotesInput ? 'active' : ''}`}
              onClick={() => setShowNotesInput(!showNotesInput)}
              title={showNotesInput ? "Ocultar notas" : "Agregar notas"}
            >
              <JournalText size={20} />
            </Button>
            <Button variant="none" onClick={handleCloseModal}>
              <X size={24} />
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body>
          {showInfo && <InfoSection />}
          {loading ? <MiniLoadingSpinner left="50%" /> : renderForm()}
          
          {/* Notes Input Section */}
          {showNotesInput && (
            <div className="notes-section">
              <div className="notes-header">
                <JournalText size={18} />
                <span>Notas del Arqueo</span>
              </div>
              <Form.Group className="notes-input-group">
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Agregar notas sobre este arqueo de caja (opcional)..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="notes-textarea"
                  maxLength={500}
                />
                <div className="notes-counter">
                  {notes.length}/500 caracteres
                </div>
              </Form.Group>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            <div className="footer-left">
              {notes.trim() && (
                <div className="notes-indicator">
                  <JournalText size={14} />
                  <span>Notas agregadas</span>
                </div>
              )}
            </div>
            <div className="footer-right">
              <Button variant="none" className="form-button-cancel" onClick={handleCloseModal}>
                <span className="btn-icon"><X size={16} /></span>
                <span className="btn-text">Cancelar</span>
              </Button>
              {!hideCloseSession && (
                <Button
                  variant="none"
                  className="form-button-create"
                  disabled={loading || approving || isProcessingWorkflow}
                  onClick={handleCreateAndClose}
                  title="Crear arqueo y cerrar sesión"
                >
                  <span className="btn-icon"><CheckCircle size={16} /></span>
                  <span className="btn-text">{isProcessingWorkflow ? 'Procesando...' : 'Crear y cerrar sesión'}</span>
                </Button>
              )}
              <Button variant="none" className="form-button-create" disabled={loading || approving || !hasAllInputsFilled} onClick={handleApprove}>
                {approving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  </>
                ) : (
                  <>
                    <span className="btn-icon"><CheckCircle size={16} /></span>
                    <span className="btn-text">Aprobar</span>
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Confirmation & Progress Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => {
          if (isProcessingWorkflow) return; // prevent closing mid-process
          setShowConfirmModal(false);
          setCurrentStep('setup');
          setRecordCreated(false);
          setSessionEnded(false);
        }}
        centered
        className="bundled-workflow-modal"
        backdrop={isProcessingWorkflow ? 'static' : true}
        keyboard={!isProcessingWorkflow}
      >
        <Modal.Header>
          <div className="form-modal-title-container">
            <Modal.Title className="form-modal-title">Confirmar acción</Modal.Title>
          </div>
          {!isProcessingWorkflow && (
            <Button variant="none" onClick={() => {
              setShowConfirmModal(false);
              setCurrentStep('setup');
              setRecordCreated(false);
              setSessionEnded(false);
            }}>
              <X size={24} />
            </Button>
          )}
        </Modal.Header>
        <Modal.Body>
          {currentStep === 'setup' && (
            <div className="warning-banner workflow-warning">
              <div className="warning-icon"><ExclamationTriangle size={20} /></div>
              <div className="warning-content">
                <div className="warning-title">Acción irreversible</div>
                <p className="warning-message">Esta acción creará el arqueo y cerrará la sesión de caja actual inmediatamente. No es reversible.</p>
                <p className="warning-message subtle">Se redirigirá al panel principal al finalizar.</p>
              </div>
            </div>
          )}
          {currentStep !== 'setup' && (
            <div className="workflow-steps">
              <ul>
                <li className={recordCreated ? 'done' : currentStep === 'creating-record' ? 'active' : ''}>
                  <div className="step-icon">
                    {recordCreated ? <CheckCircle /> : currentStep === 'creating-record' ? <Spinner animation="border" size="sm" /> : <Play />}
                  </div>
                  <span>Creando arqueo</span>
                </li>
                <li className={sessionEnded ? 'done' : currentStep === 'ending-session' ? 'active' : ''}>
                  <div className="step-icon">
                    {sessionEnded ? <CheckCircle /> : currentStep === 'ending-session' ? <Spinner animation="border" size="sm" /> : <ArrowRight />}
                  </div>
                  <span>Cerrando sesión</span>
                </li>
                <li className={currentStep === 'completed' ? 'done' : ''}>
                  <div className="step-icon">
                    {currentStep === 'completed' ? <CheckCircle /> : <ArrowRight />}
                  </div>
                  <span>Completado</span>
                </li>
              </ul>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <div className="form-modal-footer-custom">
            {currentStep === 'setup' && (
              <>
                <Button
                  variant="none"
                  className="form-button-cancel"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isProcessingWorkflow}
                >
                  Cancelar
                </Button>
                <Button
                  variant="none"
                  className="form-button-create"
                  onClick={executeBundledWorkflow}
                  disabled={isProcessingWorkflow}
                >
                  Confirmar y ejecutar
                </Button>
              </>
            )}
            {currentStep !== 'setup' && currentStep !== 'completed' && (
              <Button variant="none" className="form-button-create" disabled>
                Procesando...
              </Button>
            )}
            {currentStep === 'completed' && (
              <Button
                variant="none"
                className="form-button-create"
                onClick={() => window.location.assign('/App')}
              >
                Ir al panel
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CreateCajaRecord;
